import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n';
import { isEmpty, findIndex, size, isFunction, find, throttle } from 'lodash';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import Logo from 'components/Logo';
import Clock from 'components/Clock';
import CollectionList from './CollectionList';
import {
  StyledEmptyDataStub,
  StyledCollection,
  StyledCollectionWrapper,
  StyledCollectionTitle,
} from './StyledComponents';

import { CONTENT_TYPES } from 'constants/content-types';

const SCROLL_BY_LIST_SIZES = {
  BANNER: 545,
  SMALL_BANNER: 715,
  LARGE: 755,
  MEDIUM: 586,
};

const DEFAULT_SCROLL_SIZE = 755;

const LAST_FOCUS_KEY = 'SECTION_COLLECTION';

const getFocusBackConfig = (listId, elementIndex) => ({
  sectionToFocus: `@${listId}`,
  elementToFocus: `#${listId}-item-${elementIndex}`,
});

const onWheelHandler = throttle((deltaY) => {
  const direction = deltaY > 0 ? 'down' : 'up';
  SpatialNavigationWrapper.move(direction);
}, 300);

const CustomCollection = (props) => {
  const { title, collections, startListScrollIndex, focusedCollectionId, disableDefaultFocus, lastFocusKeeper } = props;
  const {
    top,
    left,
    small,
    showLogo,
    showClock,
    onContentEnter,
    onContentFocus,
    loadExtraItems,
    disableLastFocus = false,
  } = props;
  const lastFocus = lastFocusKeeper.getSaved(LAST_FOCUS_KEY) || {};
  const [focusedListIndex, setFocusedListIndex] = useState(() => {
    if (disableLastFocus) return 0;
    if (Number.isInteger(lastFocus.listIndex)) return lastFocus.listIndex;
    const i = findIndex(collections, { id: focusedCollectionId });
    return i !== -1 ? i : 0;
  });

  const focusedIndexToHideHeader = useMemo(
    () => (find(collections, { contentSize: CONTENT_TYPES.SMALL_BANNER }) ? 1 : 0),
    [collections],
  );

  const offset = useMemo(() => {
    const scrolled = collections.slice(0, focusedListIndex);
    const isSmallBannerExists = !!find(collections, { contentSize: CONTENT_TYPES.SMALL_BANNER });
    const isBigBannerExists = !!find(collections, { contentSize: CONTENT_TYPES.BANNER });

    return scrolled.reduce((res, curr) => {
      const contentSizeFromAssetsType = curr.contentSize;
      let step = SCROLL_BY_LIST_SIZES[contentSizeFromAssetsType] || DEFAULT_SCROLL_SIZE;
      if (isSmallBannerExists && curr.contentSize === CONTENT_TYPES.BANNER) step = 0;
      if (!isBigBannerExists && isSmallBannerExists && curr.contentSize === CONTENT_TYPES.SMALL_BANNER) step = 0;

      return res + step;
    }, 0);
  }, [collections, focusedListIndex]);

  const onEnter = useCallback(
    (item, index, list) => {
      const focusBackConfig = getFocusBackConfig(list.id, index);
      const lastFocusConfig = { listIndex: list.index, elementIndex: index };
      onContentEnter(item, index, focusBackConfig);
      lastFocusKeeper.save(LAST_FOCUS_KEY, lastFocusConfig);
    },
    [onContentEnter, lastFocusKeeper],
  );

  const onFocus = useCallback(
    (item, index, list) => {
      if (isFunction(loadExtraItems)) {
        const { id, content } = collections[list.index];
        const listSize = size(content);
        const isRequiresExtraLoad = index >= listSize - 1 - 4;
        if (isRequiresExtraLoad) loadExtraItems(id);
      }

      if (isFunction(onContentFocus)) onContentFocus();

      setFocusedListIndex(list.index);
    },
    [collections, loadExtraItems, onContentFocus],
  );
  // Clear last focus after "mount"
  useEffect(() => {
    if (!lastFocusKeeper.hasSaved(LAST_FOCUS_KEY)) return;
    // async timeout necessary here
    setTimeout(() => lastFocusKeeper.reset(LAST_FOCUS_KEY), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showLogo && (
        <Logo hide={focusedListIndex > focusedIndexToHideHeader} noAnimation>
          <Logo.Volia />
        </Logo>
      )}
      {showClock && <Clock hide={focusedListIndex > focusedIndexToHideHeader} noAnimation />}
      <StyledCollection onWheel={(e) => onWheelHandler(e.deltaY)} small={small} top={top} left={left}>
        {title && <StyledCollectionTitle offset={offset}>{title}</StyledCollectionTitle>}
        <StyledCollectionWrapper offset={offset}>
          {collections.map((collection, index) => {
            const { content, contentSize, title, id } = collection;
            const contentSizeFromAssetsType = contentSize;
            const listId = `collection-list-${id}`;
            // Default index should be taken from lastFocus
            // if current list is last focused list after navigation back
            const defaultIndex = lastFocus.listIndex === index ? lastFocus.elementIndex : 0;
            const defaultSection = !disableDefaultFocus ? index === focusedListIndex : false;
            const prevItem = collections[index - 1];
            const nextItem = collections[index + 1];
            const isItLastCollection = collections.length - 1 === index;
            // Fixes navigation probles because of sub tabs under lists
            const config = {
              leaveFor: {
                up: prevItem ? `@collection-list-${prevItem.id}` : 'default',
                down: nextItem ? `@collection-list-${nextItem.id}` : isItLastCollection ? '' : 'default',
                right: '',
              },
            };

            return (
              <CollectionList
                key={listId}
                id={listId}
                title={title}
                index={index}
                defaultIndex={disableLastFocus ? 0 : defaultIndex}
                defaultSection={defaultSection}
                startScrollIndex={startListScrollIndex}
                items={content}
                contentSize={contentSizeFromAssetsType}
                onFocus={onFocus}
                onEnter={onEnter}
                config={config}
              />
            );
          })}
        </StyledCollectionWrapper>
      </StyledCollection>
    </>
  );
};

const collectionShape = PropTypes.shape({
  content: PropTypes.array.isRequired,
  contentSize: PropTypes.string,
  title: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});

CustomCollection.propTypes = {
  title: PropTypes.string,
  collections: PropTypes.arrayOf(collectionShape).isRequired,
  startListScrollIndex: PropTypes.number,
  focusedCollectionId: PropTypes.string,
  top: PropTypes.number,
  left: PropTypes.number,
  small: PropTypes.bool,
  showLogo: PropTypes.bool,
  showClock: PropTypes.bool,
  onContentEnter: PropTypes.func.isRequired,
  loadExtraItems: PropTypes.func,
  lastFocusKeeper: PropTypes.object,
};

const CollectionContainer = (props) => {
  const { isLoading, collections } = props;
  // "isLoading" could be "undefined" before loading starts
  if (isLoading === false && isEmpty(collections)) {
    return <StyledEmptyDataStub>{i18n.t('common.nothingFound')}</StyledEmptyDataStub>;
  } else if (isEmpty(collections)) {
    return null;
  }
  return <CustomCollection {...props} />;
};

export default withKeepingLastFocus(CollectionContainer);
